import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { ApiError, api } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { AuthLayout } from './AuthLayout';

export default function Signup(): ReactElement {
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Beta privada — si signup está cerrado, mostrar mensaje en lugar del form.
  // Los emails autorizados igual pueden registrarse haciendo el POST directo
  // si llegan al form sabiendo el path; el backend valida la allowlist server-side.
  const { data: authConfig, isLoading: configLoading } = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api<{ signupEnabled: boolean }>('/auth/config'),
    staleTime: 5 * 60 * 1000,
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await signup({ name, email, password });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setError('Error inesperado. Probá de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!configLoading && !authConfig?.signupEnabled) {
    return (
      <AuthLayout title="Beta privada" subtitle="El registro está cerrado actualmente">
        <div className="p-4 rounded-lg border border-surface-border bg-surface-panel text-sm text-ink-muted leading-relaxed">
          TB Trading está en fase beta privada. El acceso es por invitación
          mientras refinamos el producto. Si tenés una invitación, contactá al
          administrador para que tu email sea agregado.
        </div>
        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-brand-500 hover:text-brand-400 font-medium"
          >
            Iniciar sesión
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Crear cuenta" subtitle="Empezá a llevar tu journal de trading hoy">
      <form onSubmit={submit} className="space-y-4">
        <Input
          label="Nombre"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
          placeholder="ej: Maxi"
          error={fieldErrors.name?.[0]}
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="vos@ejemplo.com"
          error={fieldErrors.email?.[0]}
        />
        <Input
          label="Contraseña"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          placeholder="Mínimo 8 caracteres"
          hint="Mínimo 8 caracteres"
          error={fieldErrors.password?.[0]}
        />

        {error && (
          <div className="p-3 rounded-lg border border-loss/30 bg-loss/10 text-sm text-loss">
            {error}
          </div>
        )}

        <Button type="submit" loading={submitting} className="w-full" size="lg">
          {submitting ? 'Creando…' : 'Crear cuenta'}
        </Button>
      </form>

      <p className="mt-6 text-sm text-ink-subtle text-center">
        ¿Ya tenés cuenta?{' '}
        <Link to="/login" className="text-brand-500 hover:text-brand-400 font-medium">
          Iniciar sesión
        </Link>
      </p>
    </AuthLayout>
  );
}
